import { useEffect, useRef, useState } from 'react';
import { Box, chakra } from '@chakra-ui/react';
import { Copy, Pin, PinOff } from 'lucide-react';

/**
 * How long the dismissal animation runs before the menu leaves the tree.
 * Must match the bubblePopOut duration in globals.css.
 */
const EXIT_MS = 120;

type Props = {
  open: boolean;
  onClose: () => void;
  /** Right-aligned for own messages, which sit on the right of the thread. */
  isOwn: boolean;
  /** Absent when the message has no text (an image or PDF on its own). */
  onCopy?: () => void;
  canPin: boolean;
  isPinned: boolean;
  onPin: () => void;
  onUnpin: () => void;
};

/**
 * The actions on one message, opened by a long press (or a right-click).
 *
 * Built the way ChatMenu and EventSelector are -- a positioned Box with its own
 * outside-click and Escape handling -- rather than a menu primitive. Chakra's Menu,
 * ContextMenu and ActionBar are used nowhere in this codebase, and ChatMenu's docstring
 * says that was a decision rather than an oversight.
 *
 * Copy is here for everyone, not as a convenience. Making long-press ours means the
 * bubble carries `-webkit-touch-callout: none` and `user-select: none`, which takes away
 * the platform's own way to copy a message. This gives it back -- without it the feature
 * is a net loss for every user who cannot pin.
 */
export function MessageActionMenu({
  open,
  onClose,
  isOwn,
  onCopy,
  canPin,
  isPinned,
  onPin,
  onUnpin,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  // `open` says what the parent wants; these say what is actually on screen. A component
  // cannot animate its own removal while unmounting, so it outlives `open` by EXIT_MS.
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }
    if (!mounted) return;

    setClosing(true);
    const timer = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, EXIT_MS);
    // Cleared if the menu is reopened mid-exit, which would otherwise unmount the freshly
    // opened menu a moment later.
    return () => window.clearTimeout(timer);
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!mounted) return null;

  const itemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 2.5,
    w: 'full',
    px: 3,
    py: 2.5,
    fontSize: 'sm',
    color: 'volyx.primary',
    textAlign: 'start' as const,
    cursor: 'pointer',
    bg: 'transparent',
    _hover: { bg: 'volyx.lightGray' },
  };

  return (
    <Box
      ref={ref}
      role="menu"
      aria-label="Message actions"
      position="absolute"
      // Above the bubble: a message near the bottom of the thread sits right on top of
      // the composer, and a menu opening downwards would land under the keyboard.
      bottom="calc(100% + 6px)"
      {...(isOwn ? { right: 0 } : { left: 0 })}
      zIndex={25}
      minW="180px"
      bg="volyx.bg"
      borderWidth={1}
      borderColor="volyx.borderLight"
      borderRadius="lg"
      shadow="lg"
      overflow="hidden"
      py={1}
      // Grows out of the corner nearest its own message, so the menu reads as coming from
      // the bubble rather than appearing over it. The keyframes overshoot slightly on the
      // way in -- that is what makes it pop instead of fade. `bubble-pop` is the hook the
      // prefers-reduced-motion block in globals.css swaps for a plain fade.
      className={closing ? 'bubble-pop-out' : 'bubble-pop'}
      transformOrigin={isOwn ? 'bottom right' : 'bottom left'}
      animation={
        closing
          ? `bubblePopOut ${EXIT_MS}ms cubic-bezier(0.4, 0, 1, 1) forwards`
          : 'bubblePop 0.18s cubic-bezier(0.18, 0.89, 0.32, 1.28)'
      }
      // On the way out it is scenery: a tap landing during the fade should reach whatever
      // is behind it, not a menu item that is already halfway gone.
      pointerEvents={closing ? 'none' : 'auto'}
      aria-hidden={closing || undefined}
      // The bubble suppresses selection; the menu must not inherit that, or its labels
      // are unselectable for no reason.
      css={{ WebkitUserSelect: 'auto', userSelect: 'auto' }}
      onClick={(e) => e.stopPropagation()}
    >
      {onCopy ? (
        <chakra.button
          type="button"
          role="menuitem"
          {...itemStyles}
          onClick={() => {
            onCopy();
            onClose();
          }}
        >
          <Copy size={16} />
          Copy text
        </chakra.button>
      ) : null}

      {canPin ? (
        <chakra.button
          type="button"
          role="menuitem"
          {...itemStyles}
          onClick={() => {
            if (isPinned) onUnpin();
            else onPin();
            onClose();
          }}
        >
          {isPinned ? <PinOff size={16} /> : <Pin size={16} />}
          {isPinned ? 'Unpin message' : 'Pin message'}
        </chakra.button>
      ) : null}
    </Box>
  );
}
